/** Standard dartboard order clockwise from top (20). */
export const BOARD_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];

/** Normalized radii (center → outer double wire = 1). Tuned for typical printed overlays. */
export const RINGS = {
  doubleBull: 0.07,
  singleBull: 0.16,
  tripleInner: 0.48,
  tripleOuter: 0.55,
  doubleInner: 0.78,
  doubleOuter: 1.0,
};

/**
 * Map a point on the calibrated board to a dart score.
 * @param {number} x - pixel x
 * @param {number} y - pixel y
 * @param {{cx:number,cy:number,r:number,rotationDeg?:number}} cal - circle calibration
 * @returns {{ label:string, value:number, segment:number|null, ring:string, miss:boolean }}
 */
export function scoreFromPoint(x, y, cal) {
  if (!cal || !cal.r || cal.r <= 0) {
    return { label: 'M', value: 0, segment: null, ring: 'miss', miss: true };
  }
  const dx = x - cal.cx;
  const dy = y - cal.cy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const rn = dist / cal.r;
  if (rn > RINGS.doubleOuter * 1.05) {
    return { label: 'M', value: 0, segment: null, ring: 'miss', miss: true };
  }
  if (rn <= RINGS.doubleBull) {
    return { label: 'DB', value: 50, segment: 25, ring: 'doubleBull', miss: false };
  }
  if (rn <= RINGS.singleBull) {
    return { label: 'SB', value: 25, segment: 25, ring: 'singleBull', miss: false };
  }

  // Angle: 0 at top (20), clockwise. atan2(x, -y) gives 0 at top.
  let deg = (Math.atan2(dx, -dy) * 180) / Math.PI; // -180..180
  const rot = cal.rotationDeg || 0;
  deg = ((deg - rot) % 360 + 360) % 360;
  // Each segment is 18°. Center of 20 is at 0°.
  let idx = Math.floor((deg + 9) / 18) % 20;
  const segment = BOARD_ORDER[idx];

  let ring = 'single';
  let mult = 1;
  if (rn >= RINGS.tripleInner && rn <= RINGS.tripleOuter) {
    ring = 'triple';
    mult = 3;
  } else if (rn >= RINGS.doubleInner && rn <= RINGS.doubleOuter) {
    ring = 'double';
    mult = 2;
  }

  const value = segment * mult;
  const prefix = mult === 3 ? 'T' : mult === 2 ? 'D' : 'S';
  return { label: `${prefix}${segment}`, value, segment, ring, miss: false };
}

/** Fit a circle from 3+ points (least-squares algebraic). Falls back to bounding circle of 4 corners. */
export function fitCircleFromPoints(points) {
  if (!points || points.length < 3) return null;
  if (points.length === 4) {
    // Treat as corners of board bounding box → center + radius to mid-side average
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const r =
      points.reduce((sum, p) => sum + Math.hypot(p.x - cx, p.y - cy), 0) / points.length;
    return { cx, cy, r, rotationDeg: 0 };
  }
  // Simple average center + mean radius
  const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
  const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
  const r = points.reduce((s, p) => s + Math.hypot(p.x - cx, p.y - cy), 0) / points.length;
  return { cx, cy, r, rotationDeg: 0 };
}

export function emptyVisit() {
  return [null, null, null];
}

export function visitTotal(darts) {
  return (darts || []).reduce((s, d) => s + (d && typeof d.value === 'number' ? d.value : 0), 0);
}

export function formatDart(d) {
  if (!d) return '—';
  return d.label || String(d.value ?? '—');
}

/** Session 3-dart average from 501 history for one player index. Busts count as 0 points; darts length defaults to 3. */
export function threeDartAverage(history, playerIdx) {
  let points = 0;
  let darts = 0;
  (history || []).forEach((h) => {
    if (!h || h.playerIdx !== playerIdx) return;
    const n = Array.isArray(h.darts) && h.darts.length ? h.darts.length : 3;
    darts += n;
    points += h.bust ? 0 : (Number(h.thrown) || 0);
  });
  if (darts <= 0) return null;
  return Math.round((points / (darts / 3)) * 10) / 10;
}
